/**
 * Reimagined Notifications Service
 *
 * Drop-in replacement for `@frontile/notifications` NotificationsService.
 * Uses the reimagined Toast component for rendering.
 *
 * ## Migration
 *
 * 1. With `app-js` re-exports enabled in `tio-ui/package.json`, this
 *    service is automatically registered as `service:notifications` in
 *    consuming apps — no per-app file is required.
 *
 * 2. (Optional) If your app does not consume addon `app-js` re-exports or you
 *    need a local wrapper/override, create `app/services/notifications.ts`
 *    that re-exports or extends this service:
 *    ```ts
 *    export { default } from 'tio-ui/services/notifications';
 *    ```
 *
 * 3. Replace `NotificationsContainer` in `application.gts`:
 *    ```gts
 *    import { NotificationsContainer } from 'tio-ui/components/notifications';
 *    <NotificationsContainer />
 *    ```
 *
 * 4. Update type imports in consuming components:
 *    ```ts
 *    import type { NotificationsService } from 'tio-ui/components/notifications';
 *    ```
 *
 * The `add(message, options)` API is identical to @frontile so existing call
 * sites (`this.notifications.add('Saved!', { appearance: 'success' })`) work
 * without any changes.
 */

import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { ToastItem } from '../components/toast';
import type { ToastIntent } from '../color-tokens';

// ---------------------------------------------------------------------------
// Types — API-compatible with @frontile/notifications
// ---------------------------------------------------------------------------

export interface CustomAction {
  label: string;
  onClick: () => void;
}

export interface NotificationOptions {
  /** Whether the close button is shown. @defaultValue true */
  allowClosing?: boolean;
  /** Preserve the notification (no auto-dismiss). @defaultValue false */
  preserve?: boolean;
  /** Auto-dismiss duration in ms. @defaultValue 5000 */
  duration?: number;
  /** Visual appearance. @defaultValue 'info' */
  appearance?: 'info' | 'success' | 'warning' | 'error';
  /** Optional action button rendered on the toast. Only one action is supported. */
  customAction?: CustomAction;
  /** Reimagined toast compatibility action label. Prefer customAction for new notifications usage. */
  actionLabel?: string;
  /** Reimagined toast compatibility action handler. Prefer customAction for new notifications usage. */
  onAction?: () => void;
}

export interface ReimaginedToastOptions {
  /** Duration in ms before auto-dismiss. 0 = persistent. */
  duration?: number;
  /** Optional action label */
  actionLabel?: string;
  /** Callback when action is clicked */
  onAction?: () => void;
}

// Map @frontile "appearance" to our "intent" (they happen to be the same values)
const APPEARANCE_TO_INTENT: Record<string, ToastIntent> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

const DEFAULT_DURATIONS: Record<ToastIntent, number> = {
  success: 5000,
  info: 5000,
  warning: 8000,
  error: 0,
};

/** Transition exit duration in ms — matches the CSS `duration-300` class. */
const EXIT_TRANSITION_MS = 300;

let nextId = 0;

export default class NotificationsService extends Service {
  /**
   * Reactive array of active toasts. Bind to `<NotificationsContainer>`.
   * Use `@tracked` on the array reference so Glimmer re-renders on mutations.
   */
  @tracked toasts: ToastItem[] = [];

  // Timer tracking — prevents leaks and post-destroy mutations
  private rafIds = new Map<string, number>();
  private autoDismissTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private removalTimers = new Map<string, ReturnType<typeof setTimeout>>();

  // -----------------------------------------------------------------------
  // Public API (same as @frontile NotificationsService)
  // -----------------------------------------------------------------------

  /**
   * Show a notification.
   *
   * ```ts
   * this.notifications.add('Saved!', { appearance: 'success' });
   * this.notifications.add('Oops', { appearance: 'error', preserve: true });
   * ```
   */
  add = (
    message: string,
    optionsOrIntent?: NotificationOptions | ToastIntent,
    toastOptions?: ReimaginedToastOptions
  ): ToastItem => {
    const options =
      typeof optionsOrIntent === 'string'
        ? this.notificationOptionsForIntent(optionsOrIntent, toastOptions)
        : optionsOrIntent;
    const id = `toast-${++nextId}`;
    const intent = APPEARANCE_TO_INTENT[options?.appearance ?? 'info'] ?? 'info';
    const duration = options?.preserve ? 0 : (options?.duration ?? 5000);
    const actionLabel = options?.customAction?.label ?? options?.actionLabel;
    const onAction = options?.customAction?.onClick ?? options?.onAction;
    const allowClosing = options?.allowClosing ?? true;

    const toast: ToastItem = {
      id,
      message,
      intent,
      duration,
      visible: false, // starts invisible, we flip to true on next frame for transition
      allowClosing,
      ...(actionLabel ? { actionLabel } : {}),
      ...(onAction ? { onAction } : {}),
    };

    // Push into tracked array (new reference for reactivity)
    this.toasts = [...this.toasts, toast];

    // Trigger enter transition on next frame
    const rafId = requestAnimationFrame(() => {
      this.rafIds.delete(id);
      if (this.isDestroying) return;
      this.toasts = this.toasts.map((t) => (t.id === id ? { ...t, visible: true } : t));
    });
    this.rafIds.set(id, rafId);

    // Schedule auto-dismiss
    if (duration > 0) {
      const timerId = setTimeout(() => {
        this.autoDismissTimers.delete(id);
        this.dismiss(id);
      }, duration);
      this.autoDismissTimers.set(id, timerId);
    }

    return toast;
  };

  success = (message: string, options?: ReimaginedToastOptions): ToastItem => {
    return this.add(message, 'success', options);
  };

  error = (message: string, options?: ReimaginedToastOptions): ToastItem => {
    return this.add(message, 'error', options);
  };

  warning = (message: string, options?: ReimaginedToastOptions): ToastItem => {
    return this.add(message, 'warning', options);
  };

  info = (message: string, options?: ReimaginedToastOptions): ToastItem => {
    return this.add(message, 'info', options);
  };

  /**
   * Dismiss a single toast by id.
   */
  dismiss = (id: string): void => {
    // Cancel any pending RAF or auto-dismiss for this toast
    this.cancelTimersForToast(id);

    // Trigger exit transition
    this.toasts = this.toasts.map((t) => (t.id === id ? { ...t, visible: false } : t));

    // Remove from DOM after transition completes
    const timerId = setTimeout(() => {
      this.removalTimers.delete(id);
      if (this.isDestroying) return;
      this.toasts = this.toasts.filter((t) => t.id !== id);
    }, EXIT_TRANSITION_MS);
    this.removalTimers.set(id, timerId);
  };

  /**
   * Remove a notification object (compat with @frontile `remove(notification)`)
   */
  remove = (notification?: { id?: string }): void => {
    if (notification?.id) {
      this.dismiss(notification.id);
    }
  };

  /**
   * Remove all notifications.
   */
  removeAll = (): void => {
    this.clearAllTimers();
    this.toasts = [];
  };

  clear = (): void => {
    this.removeAll();
  };

  willDestroy(): void {
    this.clearAllTimers();
    this.toasts = [];
    super.willDestroy();
  }

  // -----------------------------------------------------------------------
  // Private helpers
  // -----------------------------------------------------------------------

  private cancelTimersForToast(id: string): void {
    const rafId = this.rafIds.get(id);
    if (rafId !== undefined) {
      cancelAnimationFrame(rafId);
      this.rafIds.delete(id);
    }

    const autoTimer = this.autoDismissTimers.get(id);
    if (autoTimer !== undefined) {
      clearTimeout(autoTimer);
      this.autoDismissTimers.delete(id);
    }

    const removalTimer = this.removalTimers.get(id);
    if (removalTimer !== undefined) {
      clearTimeout(removalTimer);
      this.removalTimers.delete(id);
    }
  }

  private notificationOptionsForIntent(
    intent: ToastIntent,
    options?: ReimaginedToastOptions
  ): NotificationOptions {
    const duration = options?.duration ?? DEFAULT_DURATIONS[intent] ?? 5000;
    return {
      appearance: intent,
      duration,
      preserve: duration === 0,
      ...(options?.actionLabel ? { actionLabel: options.actionLabel } : {}),
      ...(options?.onAction ? { onAction: options.onAction } : {}),
    };
  }

  private clearAllTimers(): void {
    for (const rafId of this.rafIds.values()) {
      cancelAnimationFrame(rafId);
    }
    this.rafIds.clear();

    for (const timerId of this.autoDismissTimers.values()) {
      clearTimeout(timerId);
    }
    this.autoDismissTimers.clear();

    for (const timerId of this.removalTimers.values()) {
      clearTimeout(timerId);
    }
    this.removalTimers.clear();
  }
}

// Ember service registry augmentation for `@service declare notifications`
declare module '@ember/service' {
  interface Registry {
    notifications: NotificationsService;
  }
}
